import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Sidebar } from "./components/layout";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import { logout, setAdminInfo, setToken } from "./store/slices/adminSlices";
import request, { setAuthErrorHandler } from "./utils/request";
import { performLogoutAndRedirect } from "./utils/logout";
import { getBypassAdminUser, isLocalSamlBypassEnabled } from "./utils/authBypass";
import {
  LoginScreen,
  SignupScreen,
  DashboardScreen,
  DriverListScreen,
  OrderListScreen,
  HostListScreen,
  HostDetails,
  DriverDetailsScreen,
  PropertyDetailsScreen,
  KrogerCallback
} from "./screens";
import HostPersonalDetails from "./screens/HostDetailScreen";
import OrderDetails from "./screens/OrderDetails";
import AdminPackages from "./screens/AdminPackages";
import PrivateRoutes from "./components/utilityComponents/PrivateRoutes";
import PageNotFoundScreen from "./screens/PageNotFoundScreen";
import SupportScreen from "./screens/SupportScreen";
import ParserDetailScreen from "./screens/ParserDetailScreen";
import SupportChat from "./screens/SupportChat";
import Offers from "./screens/Offers";
import OfferCreate from "./screens/OfferCreate";
import OfferEdit from "./screens/OfferEdit";
import PropertyOffers from "./screens/PropertyOffers";
import PropertyOffersCreate from "./screens/PropertyOffersCreate";
import PropertyOfferEdit from "./screens/PropertyOfferEdit";
import DriverPromotions from "./screens/DriverPromotions";
import DriverPromotionCreate from "./screens/DriverPromotionCreate";
import DriverPromotionEdit from "./screens/DriverPromotionEdit";
import DiscountsScreen from "./screens/DiscountsScreen";
import HostEntryInstructionsSettings from "./screens/HostEntryInstructionsSettings";
import HostEntryInstructionsAll from "./screens/HostEntryInstructionsAll";
import TrackOrder from "./screens/TrackOrder";
import ManageOrderStatus from "./screens/ManageOrderStatus";
import OrderFeedback from "./screens/OrderFeedback";
import GroceryListing from "./screens/GroceryListing";
import AssignPropertyStore from "./screens/AssignPropertyStore";
import AssignAdditionalDelivery from "./screens/AssignAdditionalDelivery";
import DispatchScreen from "./screens/DispatchScreen";
import CommandCenterScreen from "./screens/CommandCenterScreen";
import LeadsDashboard from "./screens/LeadsDashboard";
import InquiriesDashboard from './screens/InquiriesDashboard';
import GmailOAuthCallback from "./screens/GmailOAuthCallback";
import GmailSettingsPage from "./screens/GmailSettingsPage";
import OrderLiveScreen from "./screens/OrderLiveScreen";
import Announcements from "./screens/Announcements";
import AnnouncementCreate from "./screens/AnnouncementCreate";
import AnnouncementEdit from "./screens/AnnouncementEdit";
import AnnouncementStats from "./screens/AnnouncementStats";
import TicketQueueScreen from "./screens/TicketQueueScreen";
import TicketAdminScreen from "./screens/TicketAdminScreen";
import TicketTypeAdmin from "./screens/TicketTypeAdmin";
import DispatcherProfile from "./screens/DispatcherProfile";
import DispatcherEscalate from "./screens/DispatcherEscalate";
import KrogerCheckinTest from "./screens/KrogerCheckinTest";
import EmployeeScheduleAdmin from "./screens/EmployeeScheduleAdmin";
import OnboardingScreen from "./screens/OnboardingScreen";
import SigningAssistant from "./screens/SigningAssistant";
import ServiceRequestsScreen from "./screens/ServiceRequestsScreen";
import SubscriptionDashboardScreen from "./screens/SubscriptionDashboardScreen";
import AIConciergeScreen from "./screens/AIConciergeScreen";
import HostFeatureSettingsScreen from "./screens/HostFeatureSettingsScreen";
import ConciergeAnalyticsScreen from "./screens/ConciergeAnalyticsScreen";
import ProviderQueueScreen from "./screens/ProviderQueueScreen";
import ProviderDetailScreen from "./screens/ProviderDetailScreen";
import MarketManagementScreen from "./screens/MarketManagementScreen";
import SocialPublishingScreen from "./screens/SocialPublishingScreen";
import LinkedInOAuthCallback from "./screens/LinkedInOAuthCallback";
import ConciergeServicesAdminScreen from "./screens/ConciergeServicesAdminScreen";
import GuestPushNotificationsScreen from "./screens/GuestPushNotificationsScreen";
import GuestPushNotificationCreate from "./screens/GuestPushNotificationCreate";
import GuestEngagementScreen from "./screens/GuestEngagementScreen";

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminInfo } = useSelector((state) => state.login);
  const [isValidating, setIsValidating] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // When Redux adminInfo is cleared (e.g. logout from Header/Sidebar), sync local state
  // so the next render shows login-only (no Sidebar, no protected routes).
  useEffect(() => {
    if (!adminInfo && !isValidating) {
      setSessionValid(false);
      setCurrentUser(null);
    }
  }, [adminInfo, isValidating]);

  // Wire request interceptor: on 401/403/440, clear session and redirect to admin login.
  useEffect(() => {
    setAuthErrorHandler(() => performLogoutAndRedirect(dispatch));
    return () => setAuthErrorHandler(null);
  }, [dispatch]);

  // Validate session on app load. Do not render app content until we have received
  // the /auth/me response (success or failure). AbortController ignores stale runs (e.g. StrictMode).
  useEffect(() => {
    const abortController = new AbortController();

    const validateSession = async () => {
      if (isLocalSamlBypassEnabled()) {
        const bypassUser = getBypassAdminUser();
        localStorage.setItem('adminInfo', JSON.stringify(bypassUser));
        dispatch(setAdminInfo(bypassUser));
        setCurrentUser(bypassUser);
        setSessionValid(true);
        setIsValidating(false);
        return;
      }

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[App] Calling /auth/me to validate session...');
        }
        const response = await request.get('/auth/me', {
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;

        if (process.env.NODE_ENV !== 'production') {
          console.log('[App] /auth/me response received');
        }

        const userData = response.data?.user;
        const token = response.data?.token;

        const hasValidShape = userData && (userData.email || userData.workEmail || (userData.firstName && userData.lastName));
        if (hasValidShape) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[App] Session valid, user:', userData.email);
          }
          localStorage.setItem('adminInfo', JSON.stringify(userData));
          dispatch(setAdminInfo(userData));
          if (token) {
            dispatch(setToken(token));
          }
          setCurrentUser(userData);
          setSessionValid(true);
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[App] Rejecting minimal/stale user shape (missing email/name). user keys:', userData ? Object.keys(userData) : 'null');
          }
          throw new Error(userData ? 'User object missing email/name (invalid shape)' : 'No user data returned');
        }
      } catch (err) {
        if (abortController.signal.aborted) return;

        const status = err?.response?.status;
        const errorMessage = err?.response?.data?.error || err?.message;
        const isAbort = err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';
        if (!isAbort) {
          console.error('[App] Session validation failed:', { status, message: errorMessage || 'Unknown error' });
        }

        if (status === 401 || status === 403 || status === 440) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[App] Unauthorized - clearing session');
          }
          dispatch(logout());
          localStorage.clear();
          setSessionValid(false);
          setCurrentUser(null);
        } else if (!isAbort) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[App] Non-auth error, but clearing session for safety');
          }
          dispatch(logout());
          localStorage.clear();
          setSessionValid(false);
          setCurrentUser(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsValidating(false);
        }
      }
    };

    validateSession();
    return () => abortController.abort();
  }, []);

  // Debug logging - must be before any conditional returns (dev only)
  useEffect(() => {
    if (!isValidating && process.env.NODE_ENV !== 'production') {
      const authenticated = sessionValid && (currentUser || adminInfo);
      console.log('[App] Validation complete:', {
        sessionValid,
        currentUser: !!currentUser,
        adminInfo: !!adminInfo,
        isAuthenticated: authenticated
      });
    }
  }, [isValidating, sessionValid, currentUser, adminInfo]);

  // Authenticated only when we passed session validation AND have a proper user object.
  // Ignore stale/minimal adminInfo from localStorage (e.g. {id, role, isAdmin} only).
  const isAuthenticated = !!sessionValid && !!(currentUser || adminInfo);

  // Show loading screen while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Verifying session...</p>
        </div>
      </div>
    );
  }

  // Session validation is done and failed — show login only. Do not show Sidebar or
  // protected content until we have a successful /auth/me response (sessionValid).
  if (!isValidating && !sessionValid) {
    return (
      <DarkModeProvider>
        <Routes>
          <Route path='/' element={<LoginScreen />} />
          <Route path='/login' element={<LoginScreen />} />
          <Route path="/signup" element={<SignupScreen />} />
          <Route path="*" element={<LoginScreen />} />
        </Routes>
      </DarkModeProvider>
    );
  }

  return (
    <DarkModeProvider>
      {/* <MessageAlertListener /> */}
      {/* We have to use !hostInfo condition as per logic */}
      <div
        className={`flex w-full min-h-screen bg-zinc-50 ${
          isAuthenticated ? "overflow-hidden" : "overflow-auto"
        }`}
      >
        {isAuthenticated && <Sidebar />}
        <Routes>
          <Route path='/' element={<LoginScreen />} />
          <Route path='/login' element={<LoginScreen />} />
          <Route path="/signup" element={<SignupScreen />} />
          <Route element={<PrivateRoutes />}>
            <Route path="/dashboard" element={<DashboardScreen />} />
            <Route path="/hostList" element={<HostListScreen />} />
            <Route path="/hostList/:hid" element={<HostDetails />} />
            <Route path="/hostList/:hid/parser/:prid" element={<ParserDetailScreen />} />
            <Route path="/hostList/:hid/:pid" element={<PropertyDetailsScreen />} />
            <Route path="/adminDetails" element={<HostPersonalDetails />} />
            <Route path="/driverList" element={<DriverListScreen />} />
            <Route path="/driverList/:did" element={<DriverDetailsScreen />} />
            <Route path="/orderList" element={<OrderListScreen />} />
            <Route path="/manage-order-status" element={<ManageOrderStatus />} />
            <Route path="/assign-property-store" element={<AssignPropertyStore />} />
            <Route path="/assign-additional-delivery" element={<AssignAdditionalDelivery />} />
            <Route path="/orderList/:oid" element={<OrderDetails />} />
            <Route path="/packages" element={<AdminPackages />} />
            <Route path="/support-chat" element={<SupportChat />} />
            <Route path="/orders/live" element={<OrderLiveScreen />} />
            <Route path="/orders/live/:oid" element={<OrderLiveScreen />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/offers/create" element={<OfferCreate />} />
            <Route path="/offer/edit/:oid" element={<OfferEdit />} />
            <Route path="/property-offers" element={<PropertyOffers />} />
            <Route path="/property-offers/create" element={<PropertyOffersCreate />} />
            <Route path="/property-offers/edit/:oid" element={<PropertyOfferEdit />} />
            <Route path="/driver-promotions" element={<DriverPromotions />} />
            <Route path="/driver-promotion/create" element={<DriverPromotionCreate />} />
            <Route path="/driver-promotion/edit/:pid" element={<DriverPromotionEdit />} />
            <Route path="/discounts" element={<DiscountsScreen />} />
            <Route path="/track-order/:oid" element={<TrackOrder />} />
            <Route path="/orders-feedback" element={<OrderFeedback />} />
            <Route path="/guest-feedback" element={<OrderFeedback />} />
            <Route path="/groceries" element={<GroceryListing />} />
            <Route path="/dispatch" element={<DispatchScreen />} />
            <Route path="/command-center" element={<CommandCenterScreen />} />
            <Route path="/hosts/:hid/entry-instructions" element={<HostEntryInstructionsSettings />} />
            <Route path="/hosts/entry-instructions/all" element={<HostEntryInstructionsAll />} />
            <Route path="/leads" element={<LeadsDashboard />} />
            <Route path="/inquiries" element={<InquiriesDashboard />} />
            <Route path="/kroger-callback" element={<KrogerCallback />} />
            <Route path="/oauth/callback" element={<GmailOAuthCallback/>} />
            <Route path="/settings"  element={<GmailSettingsPage/>} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/announcements/create" element={<AnnouncementCreate />} />
            <Route path="/announcements/edit/:announcementId" element={<AnnouncementEdit />} />
            <Route path="/announcements/stats" element={<AnnouncementStats />} />
            <Route path="/concierge-services" element={<ConciergeServicesAdminScreen />} />
            <Route path="/guest-push" element={<GuestPushNotificationsScreen />} />
            <Route path="/guest-push/create" element={<GuestPushNotificationCreate />} />
            <Route path="/guest-engagement" element={<GuestEngagementScreen />} />
            <Route path="/onboarding" element={<OnboardingScreen />} />
            <Route path="/signing/ops-guide" element={<SigningAssistant />} />
            <Route path="/tickets" element={<TicketQueueScreen />} />
            <Route path="/tickets/admin" element={<TicketAdminScreen />} />
            <Route path="/tickets/types" element={<TicketTypeAdmin />} />
            <Route path="/profile" element={<DispatcherProfile />} />
            <Route path="/escalate" element={<DispatcherEscalate />} />
            <Route path="/schedules" element={<EmployeeScheduleAdmin />} />
            <Route path="/kroger-checkin-test" element={<KrogerCheckinTest />} />
            <Route path="/service-requests" element={<ServiceRequestsScreen />} />
            <Route path="/subscriptions" element={<SubscriptionDashboardScreen />} />
            <Route path="/ai-concierge" element={<AIConciergeScreen />} />
            <Route path="/ai-concierge/:hostId" element={<HostFeatureSettingsScreen />} />
            <Route path="/concierge-analytics" element={<ConciergeAnalyticsScreen />} />
            <Route path="/providers" element={<ProviderQueueScreen />} />
            <Route path="/providers/:id" element={<ProviderDetailScreen />} />
            <Route path="/markets" element={<MarketManagementScreen />} />
            <Route path="/social" element={<SocialPublishingScreen />} />
            <Route path="/social/oauth/callback" element={<LinkedInOAuthCallback />} />
          </Route>

          {/* Not Found ROute */}
          <Route path="/support" element={<SupportScreen />} />
          <Route path="*" element={<PageNotFoundScreen />} />
        </Routes>
      </div>
    </DarkModeProvider>
  );
};

export default App;
